import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Progress } from '../../components/ui/progress';
import { dashboardApi } from '../../services/api';
import { Users, Calendar, FileText, TrendingUp } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const ParentDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await dashboardApi.getParentStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner h-8 w-8 text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="parent-dashboard">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold">Parent Dashboard</h1>
        <p className="text-muted-foreground mt-1">Monitor your children's academic progress</p>
      </div>

      {/* Children Cards */}
      {stats?.children?.length > 0 ? (
        stats.children.map((child, index) => (
          <Card key={child.student?.id || index} className="border border-border animate-fadeInUp" style={{ animationDelay: `${index * 0.1}s` }}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-primary font-bold">{child.student?.name?.charAt(0)}</span>
                </div>
                <div>
                  <span className="text-xl">{child.student?.name}</span>
                  <p className="text-sm text-muted-foreground font-normal">Roll: {child.student?.roll_number}</p>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="p-4 rounded-lg bg-muted/50">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="h-4 w-4 text-primary" />
                    <span className="text-sm text-muted-foreground">Attendance</span>
                  </div>
                  <p className={`text-2xl font-bold ${child.attendance_percentage >= 75 ? 'text-green-500' : 'text-red-500'}`}>
                    {child.attendance_percentage}%
                  </p>
                  <Progress value={child.attendance_percentage} className="mt-2" />
                </div>

                <div className="p-4 rounded-lg bg-muted/50">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="h-4 w-4 text-secondary" />
                    <span className="text-sm text-muted-foreground">Academic Score</span>
                  </div>
                  <p className={`text-2xl font-bold ${child.marks_percentage >= 60 ? 'text-green-500' : 'text-red-500'}`}>
                    {child.marks_percentage}%
                  </p>
                  <Progress value={child.marks_percentage} className="mt-2" />
                </div>

                <div className="p-4 rounded-lg bg-muted/50">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="h-4 w-4 text-green-500" />
                    <span className="text-sm text-muted-foreground">Present Days</span>
                  </div>
                  <p className="text-2xl font-bold">{child.present_days}</p>
                  <p className="text-xs text-muted-foreground mt-1">out of {child.total_days} days</p>
                </div>

                <div className="p-4 rounded-lg bg-muted/50">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="h-4 w-4 text-purple-500" />
                    <span className="text-sm text-muted-foreground">Class</span>
                  </div>
                  <p className="text-2xl font-bold">{child.student?.class_id || 'N/A'}</p>
                </div>
              </div>

              {/* Performance Chart */}
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={[
                    { name: 'Attendance', value: child.attendance_percentage },
                    { name: 'Academics', value: child.marks_percentage }
                  ]}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis dataKey="name" className="text-muted-foreground" />
                    <YAxis domain={[0, 100]} className="text-muted-foreground" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Bar dataKey="value" fill="#4F46E5" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        ))
      ) : (
        <Card className="border border-border">
          <CardContent className="py-12">
            <p className="text-muted-foreground text-center">No children linked to your account</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ParentDashboard;
